<template>
    <v-card ref="permissionsManager">
        <v-card-title>
            User Management
            <v-spacer></v-spacer>
            <v-tooltip v-if="getAccess()" bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn :color="primaryColor" dark small v-bind="attrs" v-on="on" @click.stop="openAddUserDialog()">
                        <v-icon>mdi-account-plus</v-icon>
                    </v-btn>
                </template>
                <span>Add User</span>
            </v-tooltip>
            <v-dialog v-model="addUserDialog" max-width="960" persistent>
                <v-card ref="addUserCard">
                    <v-card-title>Add User</v-card-title>
                    <v-card-text class="pb-0">
                        <v-form ref="userSearchForm" v-model="valid" lazy-validation>
                            <v-text-field v-model="searchTerm" label="Search Term" :rules="searchTermRules" required
                                @keydown.enter="search()">
                            </v-text-field>
                            <v-select v-model="type" :items="typeItems" label="Type" placeholder="Type" required>
                            </v-select>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn ref="searchBtn" class="white--text" :color="primaryColor" @click="search()">
                            Search
                        </v-btn>
                        <v-btn ref="resetBtn" :color="warningColor" @click="resetSearch()">
                            Reset
                        </v-btn>
                    </v-card-actions>
                    <v-divider class="pb-2"></v-divider>
                    <v-card-text class="pb-0">
                        <v-data-table :headers="searchHeaders" :items="searchItems" sort-by="cn">
                            <template v-slot:item.actions="{ item }">
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn :color="successColor" icon v-bind="attrs" v-on="on"
                                            @click="addUser(item)">
                                            <v-icon>
                                                mdi-account-check
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Add User</span>
                                </v-tooltip>
                            </template>
                        </v-data-table>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn :color="warningColor" @click="closeAddUserDialog()">
                            Close
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card-title>
        <v-card-text>
            <v-data-table :headers="permissionsHeaders" :items="permissionsItems" sort-by="cn">
                <template v-slot:item.adminapplicants="{ item }">
                    <v-chip dark :color="getPermissionColor(item.permissions.adminapplicants)" small>
                        {{ getPermissionAccess(item.permissions.adminapplicants) }}
                    </v-chip>
                </template>
                <template v-slot:item.adminresidents="{ item }">
                    <v-chip dark :color="getPermissionColor(item.permissions.adminresidents)" small>
                        {{ getPermissionAccess(item.permissions.adminresidents) }}
                    </v-chip>
                </template>
                <template v-slot:item.adminnotifications="{ item }">
                    <v-chip dark :color="getNotificationPermissionColor(item.permissions)" small>
                        {{ getNotificationsPermissionAccess(item.permissions) }}
                    </v-chip>
                </template>
                <template v-slot:item.adminpermissions="{ item }">
                    <v-chip dark :color="getPermissionColor(item.permissions.adminpermissions)" small>
                        {{ getPermissionAccess(item.permissions.adminpermissions) }}
                    </v-chip>
                </template>
                <template v-slot:item.adminreports="{ item }">
                    <v-chip dark :color="getPermissionColor(item.permissions.adminreports)" small>
                        {{ getPermissionAccess(item.permissions.adminreports) }}
                    </v-chip>
                </template>
                <template v-slot:item.adminresidenceassistantmanager="{ item }">
                    <v-chip dark :color="getPermissionColor(item.permissions.adminresidenceassistantmanager)" small>
                        {{ getPermissionAccess(item.permissions.adminresidenceassistantmanager) }}
                    </v-chip>
                </template>
                <template v-slot:item.adminstatistics="{ item }">
                    <v-chip dark :color="getPermissionColor(item.permissions.adminstatistics)" small>
                        {{ getPermissionAccess(item.permissions.adminstatistics) }}
                    </v-chip>
                </template>
                <template v-slot:item.adminroommatematcher="{ item }">
                    <v-chip dark :color="getPermissionColor(item.permissions.adminroommatematcher)" small>
                        {{ getPermissionAccess(item.permissions.adminroommatematcher) }}
                    </v-chip>
                </template>
                <template v-slot:item.adminsystemconfiguration="{ item }">
                    <v-chip dark :color="getPermissionColor(item.permissions.adminsystemconfiguration)" small>
                        {{ getPermissionAccess(item.permissions.adminsystemconfiguration) }}
                    </v-chip>
                </template>
                <template v-if="getAccess()" v-slot:item.actions="{ item }">
                    <permissions-edit :item="item" @refreshData="refreshData" @setNotification="setNotification">
                    </permissions-edit>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn :color="dangerColor" icon v-bind="attrs" v-on="on"
                                @click.stop="openDeleteUserDialog(item)">
                                <v-icon>
                                    mdi-delete
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Remove User</span>
                    </v-tooltip>
                </template>
            </v-data-table>
            <v-dialog v-model="deleteUserDialog" max-width="540">
                <v-card ref="deleteUserCard">
                    <v-card-title>Remove User</v-card-title>
                    <v-card-text>
                        Are you sure you would like to remove {{ currentUser.cn }} from the Residence Application
                        Portal? Please note that this action cannot be undone! Please click confirm to proceed.
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn ref="deleteBtn" :color="dangerColor" text @click="deleteUser()">
                            Confirm
                        </v-btn>
                        <v-btn :color="warningColor" text @click="closeDeleteUserDialog()">
                            Cancel
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card-text>
    </v-card>
</template>

<script>
import PermissionsEdit from './EditPermissions';

export default {
    name: 'permissions-manager',
    components: {
        PermissionsEdit
    },
    props: {
        permission: {
            required: true,
            type: Object
        }
    },
    data() {
        return {
            addUserDialog: false,
            currentUser: '',
            deleteUserDialog: false,
            permissionsHeaders: [{
                    text: 'Name',
                    align: 'start',
                    value: 'cn',
                },
                {
                    text: 'Department',
                    value: 'department'
                },
                {
                    sortable: false,
                    text: 'Applicants/Forms',
                    value: 'adminapplicants'
                },
                {
                    sortable: false,
                    text: 'Residents',
                    value: 'adminresidents'
                },
                {
                    sortable: false,
                    text: 'Notifications',
                    value: 'adminnotifications'
                },
                {
                    sortable: false,
                    text: 'Permissions',
                    value: 'adminpermissions'
                },
                {
                    sortable: false,
                    text: 'Reports',
                    value: 'adminreports'
                },
                {
                    sortable: false,
                    text: 'RA Manager',
                    value: 'adminresidenceassistantmanager'
                },
                {
                    sortable: false,
                    text: 'Statistics',
                    value: 'adminstatistics'
                },
                {
                    sortable: false,
                    text: 'Pairing&Placement',
                    value: 'adminroommatematcher'
                },
                {
                    sortable: false,
                    text: 'System Configuration',
                    value: 'adminsystemconfiguration'
                },
                {
                    text: 'Actions',
                    value: 'actions',
                    sortable: false
                }
            ],
            permissionsItems: [],
            refreshTimer: '',
            searchHeaders: [{
                    text: 'Name',
                    align: 'start',
                    value: 'cn',
                },
                {
                    text: 'Department',
                    value: 'department',
                },
                {
                    text: 'Mail',
                    value: 'mail',
                },
                {
                    sortable: false,
                    text: 'Actions',
                    value: 'actions'
                }
            ],
            searchItems: [],
            searchTerm: '',
            searchTermRules: [
                v => !!v || 'Search term is required',
                v => (v && v.length >= 2) || 'Search term must be more than 2 characters',
            ],
            type: 'people',
            typeItems: [{
                text: 'Department',
                value: 'department'
            }, {
                text: 'People',
                value: 'people'
            }],
            valid: true
        }
    },
    methods: {
        addUser(item) {
            const that = this;
            const formData = new FormData();
            let api = `https://${location.hostname}/cgi-bin/res-app-api/controller.api.pl`;
            let bool = true;
            let user = {
                cn: item.cn,
                department: item.department,
                mail: item.mail,
                permissions: {
                    adminapplicants: {
                        enabled: "0",
                        name: "Applicants"
                    },
                    adminresidents: {
                        enabled: "0",
                        name: "Residents"
                    },
                    adminnotifications: {
                        enabled: "0",
                        name: "Notifications"
                    },
                    adminpermissions: {
                        enabled: "0",
                        name: "Permissions"
                    },
                    adminreports: {
                        enabled: "0",
                        name: "Reports"
                    },
                    adminresidenceassistantmanager: {
                        enabled: "0",
                        name: "Residence Assistant Manager"
                    },
                    adminstatistics: {
                        enabled: "0",
                        name: "Statistics"
                    },
                    adminroommatematcher: {
                        enabled: "0",
                        name: "Pairing&Placement"
                    },
                    adminsystemconfiguration: {
                        enabled: "0",
                        name: "System Configuration"
                    }
                },
                username: item.sAMAccountName
            };

            // Loading icon
            this.$refs.addUserCard.loading = true;

            // Check if user exists
            for (let i = 0; i < this.permissionsItems.length; i++) {
                if (this.permissionsItems[i].username == user.username) {
                    bool = false;
                    break;
                }
            }

            // Update if the user doesn't currently exist
            if (bool) {
                // Parse array into string
                formData.append('mode', 'setPermissions');
                formData.append('user', JSON.stringify(user));

                // Post request
                fetch(api, {
                        method: 'POST',
                        body: formData
                    })
                    .then(function (response) {
                        // Return JSON response
                        return response.json();
                    })
                    .then(function (json) {
                        // Success result
                        if (json.result.match(/OK/i)) {
                            that.$emit('setNotification', '#1dc9b7', 'mdi-account-check', `${user.cn} has been successfully added to the Residence Application Portal.`, true);
                        } else if (json.message.match(/UNAUTHORIZED/i)) {
                            that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `You are not authorized to perform this action. Please refresh your application.`, true);
                        } else {
                            that.$emit('setNotification', '#ffb822', 'mdi-account-alert', `${user.cn} could not be added to the Residence Application Portal. ${json.message}.`, true);
                        }

                        that.$refs.addUserCard.loading = false;
                        that.getUserPermissions();
                    })
                    .catch(function () {
                        this.$refs.addUserCard.loading = false;
                        that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `An error occurred. Please contact your system administrator.`, true);
                    });
            } else {
                this.$refs.addUserCard.loading = false;
                this.$emit('setNotification', '#ffb822', 'mdi-account-alert', `${user.cn} already exists in the Residence Application Portal.`, true);
            }
        },
        closeAddUserDialog() {
            this.addUserDialog = false;
        },
        closeDeleteUserDialog() {
            this.deleteUserDialog = false;
        },
        deleteUser() {
            const that = this;
            let api = `https://${location.hostname}/cgi-bin/res-app-api/controller.api.pl?deletePermissions&username=${this.currentUser.username}`;

            // Loader
            this.$refs.deleteBtn.loading = true;

            // GET request
            fetch(api)
                .then(function (response) {
                    // Return response
                    return response.json();
                })
                .then(function (json) {
                    if (json.result.match(/OK/i)) {
                        that.$emit('setNotification', '#1dc9b7', 'mdi-account-remove', `${that.currentUser.cn} has been successfully removed from the Residence Application Portal.`, true);
                    } else if (json.message.match(/UNAUTHORIZED/i)) {
                        that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `You are not authorized to perform this action. Please refresh your application.`, true);
                    } else {
                        that.$emit('setNotification', '#ffb822', 'mdi-account-alert', `${that.currentUser.cn} could not be removed from the Residence Application Portal. ${json.message}.`, true);
                    }

                    that.getUserPermissions();
                    that.closeDeleteUserDialog();
                })
                .catch(function () {
                    that.getUserPermissions();
                    that.closeDeleteUserDialog();
                    that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `An error occurred. Please contact your system administrator.`, true);
                })
        },
        getAccess() {
            if (this.permission.enabled == "1" && this.permission.access == "write") {
                return true;
            }

            return false;
        },
        getNotificationsPermissionAccess(permission) {
            if (permission.adminnotifications && permission.adminnotifications.enabled && permission.adminnotifications.enabled == "1") {
                if (permission.adminnotifications.access == "all") {
                    return `All`
                } else if (permission.adminnotifications.access == "applications") {
                    return `Applications`
                } else if (permission.adminnotifications.access == "creditCardPayments") {
                    return `Credit Card Payments`
                }
            }

            return `No Access`;
        },
        getNotificationPermissionColor(permission) {
            if (permission.adminnotifications && permission.adminnotifications.enabled && permission.adminnotifications.enabled == "1") {
                if (permission.adminnotifications.access) {
                    return `#1dc9b7`
                }
            }

            return `#ffb822`;
        },
        getPermissionAccess(permission) {
            if(permission){
                if (permission.enabled && permission.enabled == "1") {
                    if (permission.access && permission.access == "read") {
                        return `Read-Only`
                    } else if (permission.access && permission.access == "write") {
                        return `Full Access`
                    }
                }
            }

            return `No Access`;
        },
        getPermissionColor(permission) {
            if(permission){
                if (permission.enabled && permission.enabled == "1") {
                    if (permission.access && permission.access == "read") {
                        return `#5578eb`
                    } else if (permission.access && permission.access == "write") {
                        return `#1dc9b7`
                    }
                }
            }

            return `#ffb822`;
        },
        getUserPermissions() {
            const that = this;
            let api = `https://${location.hostname}/cgi-bin/res-app-api/controller.api.pl?getPermissions`;

            // GET request
            fetch(api)
                .then(function (response) {
                    // Return response
                    return response.json();
                })
                .then(function (json) {
                    if (json.result.match(/ERROR/i)) {
                        that.$emit('setNotification', '#ffb822', 'mdi-alert-circle', `User permissions could not be retrieved. Please contact your system administrator.`, true);
                    } else if (json.message.match(/UNAUTHORIZED/i)) {
                        that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `You are not authorized to view this resource. Please refresh your application.`, true);
                    }

                    // Validate if data is being returned 
                    if (json.data) {
                        that.permissionsItems = json.data;
                    }

                    that.$emit('setLoader', false);
                })
                .catch(function () {
                    that.$emit('setLoader', false);
                    that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', `An error occurred. Please contact your system administrator.`, true);
                })
        },
        openAddUserDialog() {
            // Reset form
            this.resetSearch();
            this.addUserDialog = true;
        },
        openDeleteUserDialog(item) {
            this.currentUser = item;
            this.deleteUserDialog = true;
        },
        refreshData() {
            this.getUserPermissions();
        },
        resetSearch() {
            if (this.$refs.userSearchForm) {
                this.$refs.userSearchForm.resetValidation();
            }

            this.searchItems = [];
            this.searchTerm = '';
            this.type = 'people';
        },
        search() {
            const that = this;
            let api = `https://${location.hostname}/cgi-bin/mkvn/controller.api.kdap.pl?search&type=${this.type}&term=${this.searchTerm}&bypassFilters=removetestaccounts`;

            // Set loading
            this.$refs.searchBtn.loading = true;

            // GET request
            fetch(api)
                .then(function (response) {
                    // Return response
                    return response.json();
                })
                .then(function (json) {
                    // Error handling
                    if (json.Warning) {
                        if (json.Warning.match(/^Please supply/i)) {
                            that.$emit('setNotification', '#ffb822', 'mdi-alert-circle', 'Please provide a search term with atleast 2 characters.', true);
                        } else {
                            that.$emit('setNotification', '#5578eb', 'mdi-account-search', `Search found 0 matching record(s) using the term "${that.searchTerm}".`, true);
                        }

                        that.searchItems = [];
                    } else {
                        that.searchItems = json;
                        that.$emit('setNotification', '#1dc9b7', 'mdi-account-search', `Search found ${json.length} matching record(s) using the term "${that.searchTerm}".`, true);
                    }

                    that.$refs.searchBtn.loading = false;
                })
                .catch(function () {
                    that.$refs.searchBtn.loading = false;
                    that.$emit('setNotification', '#f64e60', 'mdi-alert-circle', 'An error occurred. Please contact your system administrator.', true);
                })
        },
        setNotification(color, icon, message, status) {
            this.$emit('setNotification', color, icon, message, status);
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        primaryColor() {
            return '#5867dd';
        },
        successColor() {
            return '#1dc9b7';
        },
        warningColor() {
            return '#ffb822'
        }
    },
    created() {
        this.refreshData();
        this.refreshTimer = setInterval(this.refreshData, 60000);
    },
    beforeDestroy() {
        // Clear refresh function
        clearInterval(this.refreshTimer);
    },
    destroyed() {
        // Clear refresh function
        clearInterval(this.refreshTimer);
    }
}
</script>